import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Edit2 } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';

interface CreateTerritoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Territory {
  id: string;
  name: string;
  postal_codes: string[];
}

export default function CreateTerritoriesModal({
  isOpen,
  onClose
}: CreateTerritoriesModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTerritory, setNewTerritory] = useState({
    name: '',
    postal_codes: ['']
  });
  const [editingTerritory, setEditingTerritory] = useState<Territory | null>(null);
  const [deletingTerritory, setDeletingTerritory] = useState<Territory | null>(null);

  const loadTerritories = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTerritories(data || []);
    } catch (err) {
      console.error('Error loading territories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load territories');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadTerritories();
      // Add class to hide navigation header
      document.body.classList.add('modal-open-ios');
    } else {
      // Remove class to show navigation header
      document.body.classList.remove('modal-open-ios');
    }

    // Cleanup function to ensure class is removed
    return () => {
      document.body.classList.remove('modal-open-ios');
    };
  }, [isOpen]);

  const handleAddPostalCode = () => {
    setNewTerritory(prev => ({
      ...prev,
      postal_codes: [...prev.postal_codes, '']
    }));
  };

  const handleRemovePostalCode = (index: number) => {
    setNewTerritory(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.filter((_, i) => i !== index)
    }));
  };

  const handlePostalCodeChange = (index: number, value: string) => {
    setNewTerritory(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.map((code, i) => 
        i === index ? value : code
      )
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness?.id || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      // Validate inputs
      if (!newTerritory.name.trim()) {
        throw new Error('Territory name is required');
      }

      const validPostalCodes = newTerritory.postal_codes.filter(code => code.trim());
      if (validPostalCodes.length === 0) {
        throw new Error('At least one postal code is required');
      }

      if (editingTerritory) {
        // Update existing territory
        const { error: updateError } = await supabase
          .from('service_areas')
          .update({
            name: newTerritory.name.trim(),
            postal_codes: validPostalCodes
          })
          .eq('id', editingTerritory.id);

        if (updateError) throw updateError;
      } else {
        // Create territory
        const { error: createError } = await supabase
          .from('service_areas')
          .insert({
            business_id: selectedBusiness.id,
            name: newTerritory.name.trim(),
            postal_codes: validPostalCodes
          });

        if (createError) throw createError;
      }

      // Reset form and reload territories
      setNewTerritory({ name: '', postal_codes: [''] });
      setEditingTerritory(null);
      await loadTerritories();
    } catch (err) {
      console.error('Error creating/updating territory:', err);
      setError(err instanceof Error ? err.message : 'Failed to create/update territory');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTerritory = (territory: Territory) => {
    setEditingTerritory(territory);
    setNewTerritory({
      name: territory.name,
      postal_codes: territory.postal_codes.length > 0 ? territory.postal_codes : ['']
    });
  };

  const handleCancelEdit = () => {
    setEditingTerritory(null);
    setNewTerritory({ name: '', postal_codes: [''] });
  };

  const handleDeleteTerritory = async (territory: Territory) => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('service_areas')
        .delete()
        .eq('id', territory.id);

      if (deleteError) throw deleteError;

      // Close confirmation and reload territories
      setDeletingTerritory(null);
      await loadTerritories();
    } catch (err) {
      console.error('Error deleting territory:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete territory');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setNewTerritory({ name: '', postal_codes: [''] });
      setEditingTerritory(null);
      setDeletingTerritory(null);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[999999]`}
      onClick={onClose}
      style={{ zIndex: 999999 }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out md:rounded-l-2xl shadow-xl border-l border-gray-200 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
        style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between rounded-t-2xl">
            <h2 className="text-xl font-bold text-gray-900">Create Territories</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95">
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Territory Name
                </label>
                <input
                  type="text"
                  value={newTerritory.name}
                  onChange={(e) => setNewTerritory(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  placeholder="Enter territory name"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Postal Codes
                  </label>
                  <button
                    type="button"
                    onClick={handleAddPostalCode}
                    className="text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors duration-200"
                  >
                    Add Another
                  </button>
                </div>
                <div className="space-y-3">
                  {newTerritory.postal_codes.map((code, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => handlePostalCodeChange(index, e.target.value)}
                        className="flex-1 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter postal code"
                      />
                      {newTerritory.postal_codes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemovePostalCode(index)}
                          className="p-2 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200 flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 text-base font-semibold text-white bg-purple-700 rounded-2xl hover:bg-purple-600 disabled:opacity-50 transition-all duration-200 active:scale-95 shadow-lg"
                >
                  {loading ? 'Saving...' : editingTerritory ? 'Update Territory' : 'Create Territory'}
                </button>
                {editingTerritory && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-6 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-2xl hover:bg-gray-200 transition-all duration-200 active:scale-95 shadow-sm"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {/* Existing Territories */}
            {territories.length > 0 && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Existing Territories</h3>
                <div className="space-y-4">
                  {territories.map((territory) => (
                    <div key={territory.id} className="bg-gray-50 rounded-2xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                      <div className="flex justify-between items-start">
                        <h4 className="text-base font-bold text-gray-900">{territory.name}</h4>
                        <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditTerritory(territory)}
                            className="p-2 text-purple-600 hover:text-purple-700 rounded-xl hover:bg-purple-50 transition-all duration-200 active:scale-95"
                            title="Edit territory"
                        >
                            <Edit2 className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setDeletingTerritory(territory)}
                            className="p-2 text-red-600 hover:text-red-700 rounded-xl hover:bg-red-50 transition-all duration-200 active:scale-95"
                            title="Delete territory"
                          >
                            <Trash2 className="h-5 w-5" />
                        </button>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {territory.postal_codes.map((code, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-semibold bg-white text-gray-700 border border-gray-200 shadow-sm"
                          >
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingTerritory && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[999999] p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 max-w-md w-full mx-4" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-100 flex items-center justify-center">
                <Trash2 className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Territory</h3>
              <p className="text-base text-gray-600 mb-6">
                Are you sure you want to delete "<strong>{deletingTerritory.name}</strong>"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeletingTerritory(null)}
                  disabled={loading}
                  className="flex-1 px-6 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-2xl hover:bg-gray-200 disabled:opacity-50 transition-all duration-200 active:scale-95"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteTerritory(deletingTerritory)}
                  disabled={loading}
                  className="flex-1 px-6 py-3 text-base font-semibold text-white bg-red-600 rounded-2xl hover:bg-red-700 disabled:opacity-50 transition-all duration-200 active:scale-95 shadow-lg"
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
          </div>
        </div>
      </div>
    </div>
      )}
    </div>,
    document.body
  );
}